import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PlusCircle, FileBadge, MessagesSquare, ArrowRight, Check } from 'lucide-react';
import { toast } from 'sonner';

// Getting-started tour — appears once per user (any role).
// - Skippable at any time; both Skip and finish call POST /auth/onboarding/complete.
// - We only open it for logged-in users whose onboarding_completed_at is null.
// - Staff (admin/moderator) are excluded — they don't need a seller tour.
const STEPS = [
  {
    key: 'post',
    icon: PlusCircle,
    title: 'Post a listing in about 90 seconds',
    body: 'Snap a few photos, add a title, price, and location. Our editor auto-generates the SEO tags and puts a watermark on every image so your photos can\'t be lifted.',
    cta: 'Post a listing',
    goto: '/post',
  },
  {
    key: 'verify',
    icon: FileBadge,
    title: 'Verify your GST — buyers filter for it',
    body: 'A one-time GST submission unlocks a green verified badge on every listing you own. Serious industrial buyers filter for verified sellers when a purchase matters.',
    cta: 'Verify now',
    goto: '/dashboard/verification',
  },
  {
    key: 'inquiries',
    icon: MessagesSquare,
    title: 'Inquiries land here — respond fast, win the deal',
    body: 'Every buyer message about your listings arrives in one clean inbox. Faster responses close more deals — we\'ll notify you the moment a real buyer writes to you.',
    cta: 'Go to my inbox',
    goto: '/dashboard/inquiries',
  },
];

const OnboardingTour = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    // Staff don't need the seller tour
    if (user.role === 'admin' || user.role === 'moderator') return;
    if (user.onboarding_completed_at) return;
    // Give the app a beat to settle after login/reload
    const t = setTimeout(() => setOpen(true), 500);
    return () => clearTimeout(t);
  }, [user]);

  const markComplete = async () => {
    // If /onboarding/complete fails, the user will see the tour again next
    // login — that's an acceptable retry mechanism. But surface the failure
    // so the founder sees it in the console/toast during testing rather than
    // guessing why the tour re-appears.
    try {
      await api.post('/auth/onboarding/complete');
    } catch (e) {
      toast.error('Could not save your onboarding progress — the tour may reappear next login.');
      // Re-throw so callers know it failed
      throw e;
    }
    try { await refresh(); } catch (e) { /* refresh failing is non-fatal */ }
  };

  const handleSkip = async () => {
    setOpen(false);
    try { await markComplete(); } catch (e) { /* toast already shown */ }
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep(s => s + 1);
    else finish();
  };

  const finish = async () => {
    setOpen(false);
    try { await markComplete(); } catch (e) { /* toast already shown */ }
  };

  const handleCta = async () => {
    const target = STEPS[step].goto;
    setOpen(false);
    try { await markComplete(); } catch (e) { /* toast already shown */ }
    navigate(target);
  };

  if (!user) return null;
  const s = STEPS[step];
  const Icon = s.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleSkip(); }}>
      <DialogContent className='sm:max-w-lg' data-testid='onboarding-tour-dialog'>
        <DialogHeader>
          <div className='flex items-center gap-3'>
            <div className='h-10 w-10 rounded-lg bg-orange-50 border border-orange-100 flex items-center justify-center text-orange-600'>
              <Icon className='h-5 w-5' />
            </div>
            <div className='flex-1 min-w-0'>
              <DialogTitle className='text-lg' data-testid='onboarding-step-title'>{s.title}</DialogTitle>
              <div className='text-[11px] text-muted-foreground mt-0.5'>Step {step + 1} of {STEPS.length}</div>
            </div>
          </div>
          <DialogDescription className='pt-2 text-sm leading-relaxed'>
            {s.body}
          </DialogDescription>
        </DialogHeader>

        {/* Progress dots */}
        <div className='flex items-center gap-1.5 pt-1' aria-hidden>
          {STEPS.map((st, i) => (
            <span
              key={st.key}
              className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-orange-500' : i < step ? 'w-4 bg-orange-300' : 'w-4 bg-slate-200'}`}
            />
          ))}
        </div>

        <DialogFooter className='sm:justify-between gap-2 pt-2'>
          <Button variant='ghost' onClick={handleSkip} data-testid='onboarding-skip-btn'>
            Skip for now
          </Button>
          <div className='flex items-center gap-2'>
            <Button variant='outline' onClick={handleCta} data-testid='onboarding-cta-btn'>
              {s.cta}
            </Button>
            <Button onClick={handleNext} data-testid='onboarding-next-btn'>
              {step < STEPS.length - 1 ? (<>Next <ArrowRight className='h-4 w-4 ml-1' /></>) : (<>Got it <Check className='h-4 w-4 ml-1' /></>)}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingTour;
